import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import notificationApi from '../api/notificationApi';

export interface NotificationItem {
  id: number; // notification_user id
  type: string;
  payload: {
    message: string;
  };
  createdAt: Date;
  isRead: boolean;
}

export const useNotification = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (socketRef.current) return;

    const socket = io(
      process.env.REACT_APP_API_URL || 'http://localhost:4000',
      {
        transports: ['websocket'],
        withCredentials: true,
      }
    );

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
    });

    // 🔥 realtime notification
    socket.on('notification', (data: any) => {
      const newItem: NotificationItem = {
        id: data.id,
        type: data.type,
        payload: { message: data.message },
        createdAt: data.createdAt,
        isRead: false,
      };

      setNotifications((prev) => [newItem, ...prev]);
      setUnreadCount((prev) => prev + 1); // 🔥 tăng badge đúng
    });

    // 🔥 init khi login / reload
    socket.on('notification:init', (data: any[]) => {
      const formatted: NotificationItem[] = data.map((nu) => ({
        id: nu.id,
        type: nu.notification.type,
        payload: { message: nu.notification.message },
        createdAt: nu.notification.createdAt,
        isRead: nu.isRead,
      }));

      setNotifications(formatted);

      const count = formatted.filter((n) => !n.isRead).length;
      setUnreadCount(count); // 🔥 sync badge đúng theo DB
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // ✅ mark all read
  const markAllRead = useCallback(async () => {
    try {
      await notificationApi.markAllRead();

      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true }))
      );

      setUnreadCount(0); // 🔥 reset badge
    } catch (err) {
      console.error('Mark read error:', err);
    }
  }, []);

  // ✅ clear all
  const clearAll = useCallback(async () => {
    try {
      await notificationApi.clearAll();
      setNotifications([]);
      setUnreadCount(0); // 🔥 reset badge
    } catch (err) {
      console.error('Clear error:', err);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    markAllRead,
    clearAll,
  };
};